import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FounderNav } from '../components/FounderNav';
import {
  approveInstitution,
  fetchPendingInstitutions,
  inviteInstitution,
  type PendingInstitution,
} from '../lib/api';
import { alertDialog, confirmDialog } from '../lib/dialog';

/**
 * Founder-only institution lifecycle surface. Two stacked panels:
 *
 *   1. Invite — name + contact email + optional founder note. Sends
 *      a tokenized link to the contact's inbox; they complete the
 *      public wizard at /institution-onboarding?token=…
 *
 *   2. Pending — communities created by a wizard submission that are
 *      waiting on approval. Each card surfaces every wizard field
 *      (institution info, email domains, crisis endpoint, moderator
 *      emails) so the founder can verify before flipping it active.
 *      On approve: status → active, moderator users get isModerator
 *      and receive a sign-in email.
 */
export function FounderInstitutions() {
  const [stale, setStale] = useState(false);
  const [pending, setPending] = useState<PendingInstitution[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [inviting, setInviting] = useState(false);

  function load() {
    fetchPendingInstitutions().then((res) => {
      if (res.ok) setPending(res.data.pending);
      else if (res.stale) setStale(true);
      else setError(res.error);
    });
  }

  useEffect(load, []);

  async function sendInvite(e: FormEvent) {
    e.preventDefault();
    if (inviting) return;
    setInviting(true);
    setError(null);
    const result = await inviteInstitution({
      institutionName: name.trim(),
      contactEmail: email.trim(),
      note: note.trim() || undefined,
    });
    setInviting(false);
    if (result.ok) {
      await alertDialog({
        title: 'Invite sent',
        message: `${email.trim()} now has a setup link. They'll complete the wizard and you'll see the community here, pending approval. The wizard URL is also available on this page if they need a resend.\n\n${result.wizardUrl}`,
      });
      setName('');
      setEmail('');
      setNote('');
      load();
    } else {
      setError(result.error);
    }
  }

  async function approve(p: PendingInstitution) {
    if (busyId) return;
    const ok = await confirmDialog({
      title: `Approve ${p.name}?`,
      message: `Sign-ups will open at ${p.emailDomains.join(', ')}. ${p.moderatorEmails.length} moderator(s) will receive a sign-in email and get isModerator set on first login. Crisis route: ${p.crisisEscalationName} (${p.crisisEscalationContact}).`,
      confirmLabel: 'Approve & go live',
    });
    if (!ok) return;
    setBusyId(p.communityId);
    setError(null);
    const result = await approveInstitution(p.communityId);
    setBusyId(null);
    if (result.ok) {
      await alertDialog({
        title: 'Approved',
        message: `${p.name} is live. ${result.moderatorsInvited} moderator(s) emailed.`,
      });
      load();
    } else {
      setError(result.error);
    }
  }

  if (stale) {
    return (
      <main className="wide">
        <div className="card">
          <h1>Session is too old</h1>
          <p className="muted">
            Founder writes require a fresh sign-in. Reload from the home page.
          </p>
          <p>
            <Link to="/">← Home</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="wide">
      <div className="card">
        <FounderNav />
        <h1>Institutions</h1>
        <p className="muted">
          Invite a school's wellness office, review their setup, approve when ready.
        </p>
        {error && <p className="error">{error}</p>}

        <section className="user-section">
          <h2>Invite an institution</h2>
          <p className="muted">
            Sends the recipient a one-shot link to the setup wizard. They fill out the form; the
            resulting community waits here for your approval.
          </p>
          <form onSubmit={sendInvite} className="institutions-invite-form">
            <label className="field-label">
              Institution name
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={120}
                placeholder="e.g. Pilot University"
              />
            </label>
            <label className="field-label">
              Wellness-office contact email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={254}
                placeholder="e.g. counseling-director@yourschool.edu"
              />
            </label>
            <label className="field-label">
              Note for the recipient (optional)
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="A personal note that appears at the top of their setup screen."
              />
            </label>
            <button type="submit" disabled={inviting || !name.trim() || !email.trim()}>
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
          </form>
        </section>

        <section className="user-section">
          <h2>Pending approvals {pending && <span className="muted">({pending.length})</span>}</h2>
          {!pending && <p className="muted">Loading…</p>}
          {pending && pending.length === 0 && (
            <p className="muted">Nothing pending. Submissions appear here for your review.</p>
          )}
          {pending && pending.length > 0 && (
            <ul className="institutions-pending">
              {pending.map((p) => (
                <li key={p.communityId} className="institutions-pending-row">
                  <header>
                    <strong>{p.name}</strong>
                    <span className="muted">· {p.type.replace(/_/g, ' ')}</span>
                    <span className="muted institutions-pending-when">
                      submitted {new Date(p.createdAt).toLocaleDateString()}
                    </span>
                  </header>
                  <dl className="institutions-pending-dl">
                    <dt>Contact</dt>
                    <dd>
                      {p.contactName ?? '—'}
                      {p.contactRole && <span className="muted"> · {p.contactRole}</span>}
                      <br />
                      <code>{p.contactEmail}</code>
                    </dd>
                    <dt>Email domains</dt>
                    <dd>
                      {p.emailDomains.map((d) => (
                        <code key={d} className="institutions-domain">
                          {d}
                        </code>
                      ))}
                    </dd>
                    <dt>Crisis endpoint</dt>
                    <dd>
                      {p.crisisEscalationName}
                      <br />
                      <code>{p.crisisEscalationContact}</code>
                      <span className="muted"> · {p.crisisEscalationType.replace(/_/g, ' ')}</span>
                    </dd>
                    <dt>Moderators</dt>
                    <dd>
                      {p.moderatorEmails.length === 0 ? (
                        <span className="muted">(none listed)</span>
                      ) : (
                        p.moderatorEmails.map((e) => (
                          <code key={e} className="institutions-mod">
                            {e}
                          </code>
                        ))
                      )}
                    </dd>
                    {p.crisisResourceNote && (
                      <>
                        <dt>Public note</dt>
                        <dd>{p.crisisResourceNote}</dd>
                      </>
                    )}
                  </dl>
                  <div className="institutions-pending-action">
                    <button
                      type="button"
                      disabled={busyId === p.communityId}
                      onClick={() => void approve(p)}
                    >
                      {busyId === p.communityId ? 'Approving…' : 'Approve & go live'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
